import { TFunction } from 'react-i18next';
import { ReactNode } from 'react';
import moment from 'moment';
import { NavigateFunction } from 'react-router-dom';
import { CellTypeEnum, TableCell, TableColumnType, TableRow } from '../../components/Table';
import {
  AccountIcon,
  CellarIcon,
  TrendingIcon,
  InvestIcon,
  TagIcon,
  ProfitIcon,
  LossIcon,
  ContactsIcon,
  HomeIcon,
  TradeIcon,
} from '../../assets/icons';
import { PerformanceOverTime } from './types';
import { ChartDataPoint, NavigationPath, SortDirection } from '../../types/DomainTypes';
import { SideBarItemType } from '../shared/types';
import { DATA_REFS, Product } from '../../types/productType';
import { getRegions } from '../../helpers';
import { roundNumber, toInternalId } from '../../utils';
import { CurrencyFormater } from '../../types/commonTypes';
import { AccountViewType, SubjectOptionKeys } from '../Accounts/types';

export const DEFAULT_LATEST_DEAL_DATE = new Date('1699-12-31');
export const buildTableRow = (
  dataRow: Record<string, unknown>,
  columns: TableColumnType[],
  id?: string,
  className?: string,
): TableRow => {
  const cells = columns.map((col: TableColumnType) => {
    return {
      dataRef: col.dataRef,
      isVisible: col.isVisible,
      text: dataRow[col.dataRef] as string,
      className: col.cellClassName || '',
      type: col.cellType || CellTypeEnum.TD,
      cellContentTemplate: col.cellContentTemplate,
    };
  });

  return {
    id,
    className: className || '',
    cells,
    rowData: dataRow,
  };
};

type PerformanceOverTimeChart = {
  netContributions: ChartDataPoint[];
  currentHoldings: ChartDataPoint[];
};
export const getHoldingsVsInvestedChartDatasource = (dataPoints: PerformanceOverTime[]): PerformanceOverTimeChart => {
  const netContributions: PerformanceOverTimeChart['netContributions'] = [];
  const currentHoldings: PerformanceOverTimeChart['currentHoldings'] = [];
  dataPoints.forEach((dataPoint) => {
    const dataPointDate = `${dataPoint.date}T12:00:00`; // date is set at noon which  prevents the issue of timezone offsets affecting the year
    // total refunds and net contributions
    netContributions.push({
      x: new Date(dataPointDate).getTime(),
      y: dataPoint.netContributions,
      data: { ...dataPoint },
    });

    // Portfolio valuation, Pending Balance, Net proceeds from sales
    currentHoldings.push({
      x: new Date(dataPointDate).getTime(),
      y: dataPoint.currentHoldings,
      data: { ...dataPoint },
    });
  });

  return { netContributions, currentHoldings };
};

export const getSidebarIcons = (t: TFunction, navigate: NavigateFunction): SideBarItemType[] => [
  {
    title: t('common:home'),
    icon: <HomeIcon />,
    value: NavigationPath.PORTFOLIO,
  },
  {
    title: t('common:portfolio'),
    icon: <TrendingIcon />,
    value: NavigationPath.MY_PORTFOLIO,
  },
  {
    title: t('common:invest'),
    icon: <InvestIcon />,
    value: NavigationPath.INVEST,
  },
  {
    title: t('common:cellar'),
    icon: <CellarIcon />,
    value: NavigationPath.MY_CELLAR,
  },
  // {
  //   title: t('common:discover'),
  //   icon: <DiscoverIcon />,
  //   value: NavigationPath.DISCOVER,
  // },
  {
    title: t('common:trade'),
    icon: <TradeIcon />,
    value: NavigationPath.TRADE,
    onClick: () => navigate(`${NavigationPath.TRADE}`),
  },
  {
    title: t('common:aiChat'),
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M8 10h.01M12 10h.01M16 10h.01" />
      </svg>
    ),
    value: NavigationPath.PORTFOLIO_CHAT,
    onClick: () => navigate(`${NavigationPath.PORTFOLIO_CHAT}`),
  },
  {
    title: t('common:accounts'),
    icon: <AccountIcon />,
    value: NavigationPath.ACCOUNTS,
    onClick: () =>
      navigate(`${NavigationPath.ACCOUNTS}`, {
        state: { accountViewType: AccountViewType.OVERVIEW },
      }),
  },
  {
    title: t('common:contact'),
    icon: <ContactsIcon />,
    value: NavigationPath.CONTACT_US,
    onClick: () =>
      navigate(`${NavigationPath.ACCOUNTS}`, {
        state: { accountViewType: AccountViewType.CONTACT_US, subject: SubjectOptionKeys.GENERAL },
      }),
  },
];

const getSortableOptions = (columns: TableColumnType[]) => {
  return columns
    .filter((x) => {
      const isVisible = x.isVisible === true || x.isVisible === undefined;
      return (x.isSortable === undefined && isVisible) || x.isSortable;
    })
    .map((col) => ({
      id: col.dataRef,
      value: col.dataRef,
      text: col.text,
      content: <span>{col.text}</span>,
    }));
};
const regionColors = getRegions();

const cellClassName = 'text-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer font-medium';
const dateFormat = 'DD MMM YYYY';
export type PortfolioViewMode = 'case' | 'total';

export const initialiseCurrentTabVT2 = (
  translation: TFunction,
  formatter: CurrencyFormater,
  viewMode: PortfolioViewMode = 'case',
) => {
  const isCaseView = viewMode !== 'total';
  const PORTFOLIO_DETIALS_TEXTS = {
    [DATA_REFS.NAME]: translation(`portfolio:table.headers.${DATA_REFS.NAME}`),
    [DATA_REFS.UNIT]: translation(`portfolio:table.headers.${DATA_REFS.UNIT}`),
    [DATA_REFS.QUANTITY]: translation(`portfolio:table.headers.${DATA_REFS.QUANTITY}`),
    [DATA_REFS.TOTAL_COST]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_COST}`),
    [DATA_REFS.TOTAL_VALUE]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_VALUE}`),
    [DATA_REFS.PERCENT_CHANGE]: translation(`portfolio:table.headers.${DATA_REFS.PERCENT_CHANGE}`),
    [DATA_REFS.PROFIT_LOSS]: translation(`portfolio:table.headers.${DATA_REFS.PROFIT_LOSS}`),
    [DATA_REFS.REGION]: translation(`portfolio:table.headers.${DATA_REFS.REGION}`),
    [DATA_REFS.VINTAGE]: translation('portfolio:table.headers.vintage'),
    [DATA_REFS.HIGHEST_LIVE_BID]: translation(`portfolio:table.headers.${DATA_REFS.HIGHEST_LIVE_BID}`),
    [DATA_REFS.LOWEST_OFFER]: translation(`portfolio:table.headers.${DATA_REFS.LOWEST_OFFER}`),
    [DATA_REFS.DEAL_DATE]: translation(`portfolio:table.headers.${DATA_REFS.DEAL_DATE}`),
    costPerCase: translation('portfolio:table.headers.costPerCase'),
    valuePerCase: translation('portfolio:table.headers.valuePerCase'),
    plPerCase: translation('portfolio:table.headers.plPerCase'),
    totalPL: translation('portfolio:table.headers.totalPL'),
  };

  // Per-case P&L is derived on the fly (there is no such field on the holding).
  const perCasePl = (rowData?: TableRow['rowData']) => {
    const product = rowData as unknown as Product;
    return Number(product?.valuePerUnit ?? 0) - Number(product?.avgCost ?? 0);
  };

  const plCellTemplate = (pl: number) => {
    const isPositive = pl > 0;
    const isNegative = pl < 0;
    return (
      <div className="flex gap-2 items-center justify-center">
        <span
          className={`whitespace-nowrap font-semibold ${
            isPositive
              ? 'text-green-600 dark:text-green-400'
              : isNegative
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          {`${formatter.format(pl)}`}
        </span>
        <div className="w-4 h-4 flex items-center justify-center">
          {isPositive && <ProfitIcon className="w-3.5 h-3.5 text-green-500" />}
          {isNegative && <LossIcon className="w-3.5 h-3.5 text-red-500" />}
        </div>
      </div>
    );
  };

  const columns: TableColumnType[] = [
    {
      dataRef: DATA_REFS.NAME,
      isSortable: false,
      width: '19%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      className: 'px-3',
      exportFn: (value: string | number, x) => `${x?.vintage} ${value}`,
      cellType: CellTypeEnum.TH,
      cellClassName: 'text-sm font-semibold text-gray-900 dark:text-white cursor-pointer',
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']): ReactNode => {
        const rowInfo = rowData as unknown as Product;
        return (
          <div className="flex items-center py-1 min-w-0">
            <div
              style={{
                backgroundColor: regionColors.find(
                  (x) => x.id === toInternalId(`${rowData!.cultWinesAllocationRegion}`),
                )?.color,
              }}
              className="w-1 h-12 rounded-full mr-3 flex-shrink-0"
            />

            <div className="flex flex-1 min-w-0 justify-between items-center gap-2">
              <span className="flex-1 min-w-0 truncate" title={cell.text}>
                {cell.text}
              </span>
              {rowInfo.qtyForSale > 0 && (
                <div className="px-2.5 py-1 flex items-center gap-1.5 text-xs font-medium bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400 rounded-full flex-shrink-0">
                  <TagIcon className="w-3 h-3" />
                  <span>{`x${rowInfo.qtyForSale}`}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      dataRef: DATA_REFS.VINTAGE,
      className: 'text-center',
      width: '5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.VINTAGE],
      isSortable: false,
      isExportable: true,
      cellClassName,
      cellContentTemplate: (cell: TableCell) => {
        const invalid = !cell.text || cell.text === 'null' || cell.text === 'undefined';
        return <span className={invalid ? 'text-gray-400 dark:text-gray-500' : ''}>{invalid ? '—' : cell.text}</span>;
      },
    },
    {
      dataRef: DATA_REFS.REGION,
      className: 'text-center',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.REGION],
      isSortable: false,
      cellClassName,
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']) => {
        const product = rowData as unknown as Product;
        const rawRegion = product?.cultWinesAllocationRegion || product?.region || '';
        if (!rawRegion) return <span className="text-gray-400 dark:text-gray-500">—</span>;
        const label = t(`common:regions.${toInternalId(rawRegion)}`, rawRegion);
        return (
          <span
            className="inline-block bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 rounded-md px-2 py-0.5 text-xs font-semibold truncate max-w-full"
            title={label}
          >
            {label}
          </span>
        );
      },
    },
    {
      dataRef: DATA_REFS.LAST_PURCHASED_DATE,
      className: 'text-center',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.DEAL_DATE],
      cellClassName,
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']) => {
        const product = rowData as unknown as Product;
        const isValid = !moment(product?.latestDealDate).isSame(DEFAULT_LATEST_DEAL_DATE);
        return (
          <span className={`px-2 ${isValid ? '' : 'text-gray-400 dark:text-gray-500'}`}>
            {isValid ? moment(product.latestDealDate).format('DD MMM YYYY') : '—'}
          </span>
        );
      },
    },
    {
      dataRef: DATA_REFS.SANITIZED_WINE_NAME,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      isVisible: false,
      isSortable: true,
    },
    {
      dataRef: DATA_REFS.UNIT,
      className: 'text-center',
      width: '7.5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.UNIT],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.QUANTITY,
      className: 'text-center',
      width: '5.5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.QUANTITY],
      cellClassName,
    },
    // Cost — "Cost / Case" (per-case) or "Total Cost" depending on the view toggle.
    {
      dataRef: DATA_REFS.AVG_COST,
      className: 'text-center',
      width: '8.5%',
      isVisible: isCaseView,
      text: PORTFOLIO_DETIALS_TEXTS.costPerCase,
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.TOTAL_COST,
      className: 'text-center',
      width: '8.5%',
      isVisible: !isCaseView,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.TOTAL_COST],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    // Value — "Value / Case" (per-case) or "Total Value".
    {
      dataRef: DATA_REFS.VALUE_PER_UNIT,
      className: 'text-center',
      width: '8.5%',
      isVisible: isCaseView,
      text: PORTFOLIO_DETIALS_TEXTS.valuePerCase,
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.TOTAL_VALUE,
      className: 'text-center',
      width: '8.5%',
      isVisible: !isCaseView,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.TOTAL_VALUE],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.PERCENT_CHANGE,
      className: 'text-center',
      width: '6.5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.PERCENT_CHANGE],
      cellClassName,
      exportFn: (value: string | number) => `${roundNumber(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => {
        const pct = Number(cell.text);
        const isPositive = pct > 0;
        const isNegative = pct < 0;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
              isPositive
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : isNegative
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {isPositive ? '+' : ''}
            {`${roundNumber(pct)}%`}
          </span>
        );
      },
    },
    // P&L — per-case (derived) or total, depending on the view toggle.
    {
      dataRef: DATA_REFS.PROFIT_LOSS_PER_CASE,
      className: 'text-center',
      width: '7.5%',
      isVisible: isCaseView,
      isSortable: false,
      text: PORTFOLIO_DETIALS_TEXTS.plPerCase,
      cellClassName,
      exportFn: (value: string | number, x) => `${formatter.format(perCasePl(x as TableRow['rowData']))}`,
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']) =>
        plCellTemplate(perCasePl(rowData)),
    },
    {
      dataRef: DATA_REFS.PROFIT_LOSS,
      className: 'text-center',
      width: '7.5%',
      isVisible: !isCaseView,
      text: PORTFOLIO_DETIALS_TEXTS.totalPL,
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => plCellTemplate(Number(cell.text)),
    },
    {
      dataRef: DATA_REFS.HIGHEST_LIVE_BID,
      className: 'text-center',
      width: '7.5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.HIGHEST_LIVE_BID],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{cell.text ? formatter.format(Number(cell.text)) : null}</span>,
    },
    {
      dataRef: DATA_REFS.LOWEST_OFFER,
      className: 'text-center',
      width: '7.5%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.LOWEST_OFFER],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => (
        <span className="px-2">{cell.text ? formatter.format(Number(cell.text)) : null}</span>
      ),
    },
    {
      dataRef: DATA_REFS.SANITIZED_WINE_NAME,
      text: DATA_REFS.SANITIZED_WINE_NAME,
      isVisible: false,
    },
  ];

  const sortByOptions = getSortableOptions(columns);
  const defaultSortBy = () => {
    const { id, text } = sortByOptions.find((x) => x.id === DATA_REFS.SANITIZED_WINE_NAME)!;
    return {
      id,
      text,
      direction: SortDirection.ASCENDING,
    };
  };

  return {
    PORTFOLIO_DETIALS_TEXTS,
    columns,
    sortByOptions,
    defaultSortBy: defaultSortBy(),
  };
};

export const initialiseSoldTab = (translation: TFunction, formatter: CurrencyFormater) => {
  const PORTFOLIO_DETIALS_TEXTS = {
    [DATA_REFS.NAME]: translation(`portfolio:table.headers.${DATA_REFS.NAME}`),
    [DATA_REFS.DEAL_DATE]: translation(`portfolio:table.headers.${DATA_REFS.DEAL_DATE}`),
    [DATA_REFS.UNIT]: translation(`portfolio:table.headers.${DATA_REFS.UNIT}`),
    [DATA_REFS.QTY_SOLD]: translation(`portfolio:table.headers.${DATA_REFS.QTY_SOLD}`),
    [DATA_REFS.SOLD_DATE]: translation(`portfolio:table.headers.${DATA_REFS.SOLD_DATE}`),
    [DATA_REFS.COST_PER_UNIT]: translation(`portfolio:table.headers.${DATA_REFS.COST_PER_UNIT}`),
    [DATA_REFS.TOTAL_COST]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_COST}`),
    [DATA_REFS.TOTAL_VALUE]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_VALUE}`),
    [DATA_REFS.COST_PER_UNIT_INCLUDING_MANAGEMENT_FEE]: translation(
      `portfolio:table.headers.${DATA_REFS.COST_PER_UNIT}`,
    ),
    [DATA_REFS.SOLD_PER_UNIT]: translation(`portfolio:table.headers.${DATA_REFS.SOLD_PER_UNIT}`),
    [DATA_REFS.PERCENT_CHANGE]: translation(`portfolio:table.headers.${DATA_REFS.PERCENT_CHANGE}`),
    [DATA_REFS.PROFIT_LOSS]: translation(`portfolio:table.headers.${DATA_REFS.PROFIT_LOSS}`),
    [DATA_REFS.REGION]: translation(`portfolio:table.headers.${DATA_REFS.REGION}`),
    [DATA_REFS.STATUS]: translation(`portfolio:table.headers.${DATA_REFS.STATUS}`),
  };

  const columns: TableColumnType[] = [
    {
      dataRef: DATA_REFS.NAME,
      isSortable: false,
      width: '20%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      className: 'px-3',
      cellType: CellTypeEnum.TH,
      cellClassName: 'text-sm font-semibold text-gray-900 dark:text-white cursor-pointer',
      exportFn: (value: string | number, x) => `${x?.vintage} ${value}`,
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']): ReactNode => {
        const rowInfo = rowData as unknown as Product;
        const label = `${rowInfo.vintage} ${cell.text}`;
        return (
          <div className="flex items-center py-1 min-w-0">
            <div
              style={{
                backgroundColor: regionColors.find(
                  (x) => x.id === toInternalId(`${rowData?.cultWinesAllocationRegion}`),
                )?.color,
              }}
              className="w-1 h-12 rounded-full mr-3 flex-shrink-0"
            />

            <div className="flex flex-1 min-w-0 justify-between items-center gap-2">
              <span className="flex-1 min-w-0 truncate" title={label}>
                {label}
              </span>
              {rowInfo.qtyForSale > 0 && (
                <div className="px-2.5 py-1 flex items-center gap-1.5 text-xs font-medium bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400 rounded-full flex-shrink-0">
                  <TagIcon className="w-3 h-3" />
                  <span>{`x${rowInfo.qtyForSale}`}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      dataRef: DATA_REFS.SANITIZED_WINE_NAME,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      isVisible: false,
      isSortable: true,
    },
    {
      dataRef: DATA_REFS.DEAL_DATE,
      className: 'text-center ',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.DEAL_DATE],
      cellClassName,
      exportFn: (value: string | number) => `${moment(value).format(dateFormat)}`,
      cellContentTemplate: (cell: TableCell) => (
        <span className="whitespace-nowrap pl-1">{`${moment(cell.text).format(dateFormat)}`}</span>
      ),
    },
    {
      dataRef: DATA_REFS.UNIT,
      className: 'text-center',
      width: '7%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.UNIT],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.QTY_SOLD,
      className: 'text-center',
      width: '8%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.QTY_SOLD],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.SOLD_DATE,
      className: 'text-center',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.SOLD_DATE],
      cellClassName,
      exportFn: (value: string | number) => `${moment(value).format(dateFormat)}`,
      cellContentTemplate: (cell: TableCell) => (
        <span className="whitespace-nowrap">{`${moment(cell.text).format(dateFormat)}`}</span>
      ),
    },

    {
      dataRef: DATA_REFS.COST_PER_UNIT,
      className: 'text-center',
      width: '8%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.COST_PER_UNIT],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.TOTAL_COST,
      className: 'text-center',
      width: '8%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.TOTAL_COST],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.SOLD_PER_UNIT,
      className: 'text-center',
      width: '8%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.SOLD_PER_UNIT],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.TOTAL_VALUE,
      className: 'text-center',
      width: '8%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.TOTAL_VALUE],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => <span>{`${formatter.format(Number(cell.text))}`}</span>,
    },
    {
      dataRef: DATA_REFS.PERCENT_CHANGE,
      className: 'text-center',
      width: '7%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.PERCENT_CHANGE],
      cellClassName,
      exportFn: (value: string | number) => `${roundNumber(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => {
        const pct = Number(cell.text);
        const isPositive = pct > 0;
        const isNegative = pct < 0;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
              isPositive
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : isNegative
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {isPositive ? '+' : ''}
            {`${roundNumber(pct)}%`}
          </span>
        );
      },
    },
    {
      dataRef: DATA_REFS.PROFIT_LOSS,
      className: 'text-center',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.PROFIT_LOSS],
      cellClassName,
      exportFn: (value: string | number) => `${formatter.format(Number(value))}`,
      cellContentTemplate: (cell: TableCell) => {
        const pl = Number(cell.text);
        const isPositive = pl > 0;
        const isNegative = pl < 0;
        return (
          <div className="flex gap-2 items-center justify-center">
            <span
              className={`whitespace-nowrap font-semibold ${
                isPositive
                  ? 'text-green-600 dark:text-green-400'
                  : isNegative
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {`${formatter.format(pl)}`}
            </span>
            <div className="w-4 h-4 flex items-center justify-center">
              {isPositive && <ProfitIcon className="w-3.5 h-3.5 text-green-500" />}
              {isNegative && <LossIcon className="w-3.5 h-3.5 text-red-500" />}
            </div>
          </div>
        );
      },
    },
    {
      dataRef: DATA_REFS.STATUS,
      className: 'text-center',
      width: '9%',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.STATUS],
      cellClassName,
      cellContentTemplate: (cell: TableCell) => (
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            {cell.text}
          </div>
        </div>
      ),
    },
    {
      dataRef: DATA_REFS.VINTAGE,
      text: DATA_REFS.VINTAGE,
      isVisible: false,
      isExportable: true,
    },
    {
      dataRef: DATA_REFS.REGION,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.REGION],
      isVisible: false,
      isSortable: true,
    },
  ];

  const sortByOptions = getSortableOptions(columns);
  const defaultSortBy = () => {
    const { id, text } = sortByOptions.find((x) => x.id === DATA_REFS.SOLD_DATE)!;
    return {
      id,
      text,
      direction: SortDirection.DESCENDING,
    };
  };

  return {
    PORTFOLIO_DETIALS_TEXTS,
    columns,
    sortByOptions,
    defaultSortBy: defaultSortBy(),
  };
};

export const initialiseMyCellarExportColumns = (translation: TFunction) => {
  const PORTFOLIO_DETIALS_TEXTS = {
    [DATA_REFS.NAME]: translation(`portfolio:table.headers.${DATA_REFS.NAME}`),
    [DATA_REFS.DEAL_DATE]: translation(`portfolio:table.headers.${DATA_REFS.DEAL_DATE}`),
    [DATA_REFS.UNIT]: translation(`portfolio:table.headers.${DATA_REFS.UNIT}`),
    [DATA_REFS.QUANTITY]: translation(`portfolio:table.headers.${DATA_REFS.QUANTITY}`),
    [DATA_REFS.COST_PER_UNIT]: translation(`portfolio:table.headers.${DATA_REFS.COST_PER_UNIT}`),
    [DATA_REFS.TOTAL_COST]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_COST}`),
    [DATA_REFS.VALUE_PER_UNIT]: translation(`portfolio:table.headers.${DATA_REFS.VALUE_PER_UNIT}`),
    [DATA_REFS.TOTAL_VALUE]: translation(`portfolio:table.headers.${DATA_REFS.TOTAL_VALUE}`),

    [DATA_REFS.PERCENT_CHANGE]: translation(`portfolio:table.headers.${DATA_REFS.PERCENT_CHANGE}`),
    [DATA_REFS.PROFIT_LOSS]: translation(`portfolio:table.headers.${DATA_REFS.PROFIT_LOSS}`),
    [DATA_REFS.REGION]: translation(`portfolio:table.headers.${DATA_REFS.REGION}`),
    [DATA_REFS.LOCATION]: translation(`portfolio:table.headers.${DATA_REFS.LOCATION}`),
    [DATA_REFS.ROTATION_NUMBER]: translation(`portfolio:table.headers.${DATA_REFS.ROTATION_NUMBER}`),
  };

  const columns: TableColumnType[] = [
    {
      dataRef: DATA_REFS.NAME,
      isSortable: false,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      className: 'px-3',
      cellType: CellTypeEnum.TH,
      cellClassName: 'text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap flex-1 cursor-pointer',
      cellContentTemplate: (cell: TableCell, t: TFunction, rowData: TableRow['rowData']): ReactNode => {
        const rowInfo = rowData as unknown as Product;
        return (
          <div className="flex items-center py-1">
            <div
              style={{
                backgroundColor: regionColors.find(
                  (x) => x.id === toInternalId(`${rowData!.cultWinesAllocationRegion}`),
                )?.color,
              }}
              className="w-1 h-12 rounded-full mr-4 flex-shrink-0"
            />

            <div className="flex flex-1 justify-between items-center gap-4">
              <span className="flex-1">{`${rowInfo.vintage} ${cell.text}`}</span>
              {rowInfo.qtyForSale > 0 && (
                <div className="px-2.5 py-1 flex items-center gap-1.5 text-xs font-medium bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400 rounded-full">
                  <TagIcon className="w-3 h-3" />
                  <span>{`x${rowInfo.qtyForSale}`}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      dataRef: DATA_REFS.SANITIZED_WINE_NAME,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.NAME],
      isVisible: false,
      isSortable: true,
    },
    {
      dataRef: DATA_REFS.REGION,
      className: 'text-center',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.REGION],
      cellClassName,
      isSortable: false,
    },
    {
      dataRef: DATA_REFS.DEAL_DATE,
      className: 'text-center ',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.DEAL_DATE],
      cellClassName,
      cellContentTemplate: (cell: TableCell) => {
        const date = moment(cell.text);
        const isValid = date.isValid() && !date.isSame(DEFAULT_LATEST_DEAL_DATE);
        return (
          <span className={isValid ? '' : 'text-gray-400 dark:text-gray-500'}>
            {isValid ? date.format(dateFormat) : '—'}
          </span>
        );
      },
    },
    {
      dataRef: DATA_REFS.UNIT,
      className: 'text-center',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.UNIT],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.QUANTITY,
      className: 'text-center',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.QUANTITY],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.LOCATION,
      className: 'text-center',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.LOCATION],
      cellClassName,
    },
    {
      dataRef: DATA_REFS.ROTATION_NUMBER,
      className: 'text-center',
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.ROTATION_NUMBER],
      cellClassName,
    },

    {
      dataRef: DATA_REFS.VINTAGE,
      text: DATA_REFS.VINTAGE,
      isVisible: false,
      isExportable: true,
    },
    {
      dataRef: DATA_REFS.CULT_WINES_ALLOCATION_REGION,
      text: PORTFOLIO_DETIALS_TEXTS[DATA_REFS.REGION],
      isVisible: false,
      isSortable: true,
    },
    {
      dataRef: DATA_REFS.SANITIZED_WINE_NAME,
      text: DATA_REFS.SANITIZED_WINE_NAME,
      isVisible: false,
    },
  ];

  const sortByOptions = getSortableOptions(columns);
  const defaultSortBy = () => {
    const { id, text } = sortByOptions.find((x) => x.id === DATA_REFS.SANITIZED_WINE_NAME)!;
    return {
      id,
      text,
      direction: SortDirection.ASCENDING,
    };
  };

  return {
    PORTFOLIO_DETIALS_TEXTS,
    columns,
    sortByOptions,
    defaultSortBy: defaultSortBy(),
  };
};
